import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { messageApiService } from "../services/messageApiService";
import { conversationKeys } from "./conversationQueries";
import {
  fromCreateMessageRequestToCreateMessageDto,
  fromMessageDtoToMessage,
  type MessageSendIntent,
  type Message,
} from "../types/messageTypes";

const staleTime = 5 * 60 * 1000; // 5 minutes

// Query keys
export const messageKeys = {
  all: ["message"] as const,
  list: (
    businessId?: string,
    conversationId?: string,
    phoneNumberId?: string,
    userId?: string
  ) =>
    [
      ...messageKeys.all,
      "list",
      { businessId, conversationId, phoneNumberId, userId },
    ] as const,
  detail: (id: string, businessId?: string, userId?: string) =>
    [...messageKeys.all, "detail", id, { businessId, userId }] as const,
};

export const useMessages = (params?: {
  conversationId?: string;
  phoneNumberId?: string;
}, enabled = true) => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();

  return useQuery<Message[], Error>({
    queryKey: messageKeys.list(
      businessId,
      params?.conversationId,
      params?.phoneNumberId,
      user?.id
    ),
    queryFn: async () => {
      const dtos = await messageApiService.getList({
        businessId,
        conversationId: params?.conversationId,
        phoneNumberId: params?.phoneNumberId,
      });
      return dtos.map(fromMessageDtoToMessage).filter((message) =>
        message.businessId === businessId && !message.deletedAt
        && (!params?.conversationId || message.conversationId === params.conversationId)
        && (!params?.phoneNumberId || message.phoneNumberId === params.phoneNumberId));
    },
    enabled: !!user && !!businessId && enabled,
    staleTime,
  });
};

export const useMessage = (id: string) => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();
  return useQuery<Message, Error>({
    queryKey: messageKeys.detail(id, businessId, user?.id),
    queryFn: async () => {
      const dto = await messageApiService.getById(id);
      return fromMessageDtoToMessage(dto);
    },
    enabled: !!user && !!businessId && !!id,
    staleTime,
  });
};

export const useCreateMessage = () => {
  const queryClient = useQueryClient();
  const { user, getAuthGeneration } = useAuth();

  return useMutation<Message, Error, MessageSendIntent, { userId: string; generation: number }>({
    meta: { action: "message.send" },
    retry: false,
    onMutate: () => {
      if (!user) throw new Error("Sign in before sending a message.");
      return { userId: user.id, generation: getAuthGeneration() };
    },
    mutationFn: async ({ request, idempotencyKey }: MessageSendIntent) => {
      const dto = fromCreateMessageRequestToCreateMessageDto(request);
      const responseDto = await messageApiService.create(dto, idempotencyKey);
      return fromMessageDtoToMessage(responseDto);
    },
    onSuccess: (newMessage, { request }, context) => {
      if (!context || context.generation !== getAuthGeneration()) return;
      // Only update loaded lists: a send must not replace an unloaded history.
      queryClient.setQueriesData<Message[]>({
        queryKey: [...messageKeys.all, "list"],
        predicate: (query) => {
          const scope = query.queryKey[2] as {
            businessId?: string; conversationId?: string; phoneNumberId?: string; userId?: string;
          };
          return scope.businessId === request.businessId
            && scope.userId === context.userId
            && (!scope.conversationId || scope.conversationId === newMessage.conversationId)
            && (!scope.phoneNumberId || scope.phoneNumberId === newMessage.phoneNumberId);
        },
      }, (old) => old
        ? [...old.filter((message) => message.id !== newMessage.id), newMessage]
        : undefined);
      void queryClient.invalidateQueries({
        queryKey: [...messageKeys.all, "list"],
        predicate: (query) => {
          const scope = query.queryKey[2] as { businessId?: string; userId?: string };
          return scope.businessId === request.businessId && scope.userId === context.userId;
        },
      });
      void queryClient.invalidateQueries({
        queryKey: conversationKeys.all,
        predicate: (query) => {
          const scope = query.queryKey[2] as { businessId?: string; userId?: string };
          return scope.businessId === request.businessId && scope.userId === context.userId;
        },
      });
    },
  });
};
